import { useMatches, useNavigate } from '@tanstack/react-router';
import { openShortcutSheet } from '@paige/capturepoint';
import { usePrincipal } from '../../auth/AuthBoundary';
import { useReadOnlyView } from '../../shell/read-only-view';
import { inShell } from '../../shell/in-shell';
import { runsJobs } from '../../tenant/role';
import { closeHelp, openHelp } from './help-view';

/** What Help's two rows that act do here: open the keys, and start the tour (or say how Paige works). */
export interface HelpChoices {
  shortcuts: () => void;
  tour: () => void;
  /** The tour row's one line: the ready-made job's tour, or the explainer for someone who may not make a job. */
  tourLine: string;
}

/**
 * The tour makes a ready-made job (the sample tour, `POST /v1/jobs/setup-imports?sample=tour`), which the API
 * keeps to admins and job managers, against the company's five free tours: a reviewer or a scanner is refused
 * (403), and so is a read-only support view. For them Take the tour plays the short explainer instead, which makes
 * nothing and costs nothing.
 */
export function useMayTakeTour(tenantId: string): boolean {
  const principal = usePrincipal();
  const readOnly = useReadOnlyView();
  return runsJobs(principal, tenantId) && !readOnly;
}

export const TOUR_LINE = 'Two minutes on a ready-made job.';
export const EXPLAINER_LINE = 'How Paige works, in half a minute.';

/** The keys open where they work: the workbench's own F1 sheet inside a job, Paige's copy of it elsewhere. */
export function useHelpChoices(tenantId: string): HelpChoices {
  const navigate = useNavigate();
  const mayTour = useMayTakeTour(tenantId);
  const inWorkbench = useMatches({ select: (matches) => matches.some((m) => inShell(m.routeId)) });
  return {
    shortcuts: () => {
      if (!inWorkbench) return openHelp(tenantId, 'shortcuts');
      closeHelp();
      openShortcutSheet();
    },
    tour: () => {
      if (!mayTour) return openHelp(tenantId, 'explainer');
      closeHelp();
      void navigate({
        to: '/t/$tenant/jobs/new',
        params: { tenant: tenantId },
        search: { tour: 1 },
      });
    },
    tourLine: mayTour ? TOUR_LINE : EXPLAINER_LINE,
  };
}
